import styled from "styled-components"
import { theme } from "@theguarantors/ui-kit-v2/dist/design-system"
import { Button } from "@theguarantors/ui-kit-v2"
import { colors } from "@theguarantors/ui-kit-v2/dist/design-system/colors"
import { HTMLAttributes } from "react"

const screenBreakPoints = {
  zero: 0,
  xs: "370px",
  small: "480px",
  medium: "768px",
  large: "1024px",
  xlarge: "1280px",
  xxlarge: "1440px",
}
const shadowColor = "rgba(67,69,172,0.5)"
const hslaColor = "hsla(0, 0%, 100%, .5)"

export const FooterSection = styled.footer`
  padding: 8rem 0 3rem;
  background-color: #080a2d;
  overflow: hidden;
`

interface FooterContainerProps {
  bottom?: boolean
}

const footerContainerBottom = `
  margin: 4rem 0 0;
  padding: 3rem 2.4rem 0;
  border-top: .1rem solid ${hslaColor};  

  @media (min-width: ${screenBreakPoints.medium}) {
    padding: 4rem 6rem 0;
  }
`

const footerContainerTop = `
  padding: 0 3rem;

  @media (min-width: ${screenBreakPoints.medium}) {
    padding: 0 6rem;
  }
`

export const FooterContainer = styled.div<FooterContainerProps>`
  ${(props: FooterContainerProps) => (props.bottom ? footerContainerBottom : footerContainerTop)}
`

export const FooterContent = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 176rem;
`

export const FooterMainContentGrid = styled.div`
  display: grid;
  column-gap: 3rem;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    "wordmark wordmark"
    "footer-cta footer-cta"
    "footer-explore footer-company"
    "footer-social footer-social";

  @media (min-width: ${screenBreakPoints.medium}) {
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-areas:
      "wordmark wordmark wordmark"
      "footer-explore footer-company footer-cta"
      "footer-explore footer-social footer-cta";
  }

  @media (min-width: ${screenBreakPoints.large}) {
    grid-template-areas:
      "wordmark wordmark . ."
      "footer-cta footer-cta footer-explore footer-company"
      "footer-cta footer-cta footer-explore footer-social";
  }
`

export const FooterLogo = styled.div`
  grid-area: wordmark;
  gap: 3rem;
  width: 100%;
  height: auto;
  max-width: 70rem;
  margin-bottom: 3.5rem;

  @media (min-width: ${screenBreakPoints.medium}) {
    margin-bottom: 4rem;
  }
  @media (min-width: ${screenBreakPoints.large}) {
    margin-bottom: 5rem;
  }

  img {
    width: 100%;
    height: auto;
  }
`

export const FooterCTA = styled.div`
  grid-area: footer-cta;
  display: flex;
  flex-direction: row;
  justify-content: unset;
  column-gap: 3.5rem;
  margin-bottom: 3.5rem;

  @media (min-width: ${screenBreakPoints.medium}) {
    flex-direction: column;
    justify-content: space-between;
    margin-bottom: 0;
  }
`
export const FooterBCorpWrapper = styled.div`
  width: max-content;
`
export const FooterDesktopImg = styled.img`
  display: none;
  @media (min-width: ${screenBreakPoints.large}) {
    display: block;
    height: 100px;
    width: 200px;
  }
`

export const FooterMobileImg = styled.img`
  display: block;
  max-height: 6rem;
  width: auto;

  @media (min-width: ${screenBreakPoints.medium}) {
    max-height: 8rem;
  }
  @media (min-width: ${screenBreakPoints.large}) {
    display: none;
  }
`

export const FooterExplore = styled.div`
  grid-area: footer-explore;
`

export const FooterCompany = styled.div`
  display: grid;
  grid-area: footer-company;
`
export const FooterSocial = styled.div`
  grid-area: footer-social;
  display: flex;
  flex-direction: row;
  column-gap: 1.4rem;
  justify-content: flex-start;
`

export const FooterLinksColumn = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.2rem;
`

export const FooterSocialLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: unset;
  grid-column: 1 / 3;
  margin: 2rem 0 0;
  column-gap: 3.4rem;

  @media (min-width: ${screenBreakPoints.medium}) {
    column-gap: 1.4rem;
    row-gap: 1.2rem;
  }
  @media (min-width: ${screenBreakPoints.large}) {
    margin: auto 0 0;
    grid-column: 2 / 3;
  }
`

export const FooterSecondaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
  color: ${hslaColor};

  @media (min-width: ${screenBreakPoints.medium}) {
    gap: 2rem;
  }
  @media (min-width: ${screenBreakPoints.large}) {
    gap: 3rem;
  }
`

export const FooterSecondaryContentDisclaimer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2rem;
  grid-column: 1/13;
`

export const LinkColumnHeading = styled.h2`
  margin: 0;
  line-height: 2.6rem;
  color: #fff;
  font-family: ${theme.fonts.acid};
  font-size: 1.8rem;
  font-weight: 400;
  letter-spacing: -0.02em;

  @media (min-width: ${screenBreakPoints.medium}) {
    line-height: 3.4rem;
    font-size: 2.4rem;
    font-weight: 450;
  }
`

export const LinkColumnLinkItem = styled.a`
  position: relative;
  text-decoration: none;
  color: ${theme.colors.white};
  white-space: nowrap;
  font-family: ${theme.fonts.acid};
  &:nth-of-type(1) {
    margin: 0.8rem 0 0;
  }

  @media (min-width: ${screenBreakPoints.medium}) {
    &:nth-of-type(1) {
      margin: 0.2rem 0 0;
    }
  }
`

const FooterLinkSharedStyle = `
  transition: border-color 0.2s ease-in-out;
  padding: 0 0 0.3rem;
  border-bottom: 0.1rem solid transparent;
`

export const LinkColumnLinkItemTitle = styled.span<{ decorated?: boolean }>`
  ${FooterLinkSharedStyle}

  ${(props: { decorated?: boolean }) =>
    props.decorated &&
    `border-color: currentcolor;
  `}
  }
  &:hover {
    border-color: currentColor;
  }
`

interface ButtonProps extends HTMLAttributes<HTMLButtonElement> {
  newGetStartedPageFeatureFlag?: boolean
  type: "button" | "submit" | "reset"
  fontFamily?: string
}

const newCTAStyle = `
    & > span {
      font-size: 1.6rem;
      line-height: 2.6rem;
    }
    border: 1px solid white;
    &:hover {
      background: white;
      color: ${colors.v2_deepNavy};
      border: 1px solid ${colors.v2_deepNavy};
      border-radius: 10px;
    }    
`

export const FooterSignInButton = styled(Button)<ButtonProps>`
  height: 55px;
  width: 11rem;
  min-width: 11rem;
  text-align: center;
  padding: 0;

  ${(props: ButtonProps) =>
    props.newGetStartedPageFeatureFlag
      ? newCTAStyle
      : `
    font-size: 2.1rem;
    box-shadow: 0 0.5rem 1rem 0 ${shadowColor};

    &:hover {
      box-shadow: 0 1rem 2rem 0 ${shadowColor};
    }
  `}
`

export const SocialMediaItem = styled.a`
  width: 3.5rem;
  height: auto;
  color: ${theme.colors.white};

  svg {
    display: block;
    width: 100%;
    height: auto;
  }

  @media (min-width: ${screenBreakPoints.small}) {
    width: 2.5rem;
  }
  @media (min-width: ${screenBreakPoints.large}) {
    width: 2.5rem;
  }
`

export const FooterLegal = styled.div`
  padding: 3rem 6rem;
  color: white;
  background: #080a2d;
  font-size: 1.6rem;
  font-family: ${theme.fonts.acid};
  font-weight: 400;
`

export const FooterLegalDisclaimer = styled.p`
  margin: 0;
  width: 100%;
  white-space: pre-line;
  font-size: 1.4rem;

  @media (min-width: ${screenBreakPoints.medium}) {
    font-size: 1.6rem;
  }
`

export const FooterLegalLink = styled.a`
    position: relative;    
    text-decoration: none;
    transition: color .2s ease-in-out;
    color: ${hslaColor};

    &:hover {
      color: ${theme.colors.white};
    }
    
}
`

export const FooterLegalLinks = styled.nav`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem 1.5rem;
  justify-content: flex-start;
  font-size: 1.4rem;

  @media (min-width: ${screenBreakPoints.small}) {
    display: flex;
    flex-wrap: wrap;
    gap: 1.2rem 3rem;
    grid-column: 6 / 13;
  }

  @media (min-width: ${screenBreakPoints.medium}) {
    font-size: 1.6rem;
    gap: 2rem 4rem;
  }

  @media (min-width: ${screenBreakPoints.large}) {
    position: unset;
    justify-content: flex-end;
    grid-column: 4 / 13;
  }

  @media (min-width: ${screenBreakPoints.xxlarge}) {
    grid-column: 5 / 13;
    justify-content: flex-end;
    column-gap: 3.2rem;
  }
`

export const FooterLegalLinkSecondary = styled(FooterLegalLink)`
  &:hover {
    span {
      border-color: currentColor;
    }
  }
  @media (max-width: ${screenBreakPoints.medium}) {
    &:nth-of-type(even) {
      margin-left: 1.5rem;
    }
  }
`

export const FooterLegalContent = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 2.4rem 3rem;

  @media (min-width: ${screenBreakPoints.medium}) {
    row-gap: 2rem;
  }
  @media (min-width: ${screenBreakPoints.large}) {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-column: 1 / 13;
    gap: 3rem;
  }
`
export const FooterLegalCopyright = styled.small`
  font-size: 1.4rem;

  @media (min-width: ${screenBreakPoints.medium}) {
    font-size: 1.6rem;
    grid-column: 1 / 4;
  }
`
